import { useState, useEffect } from 'react';

/**
 * Custom hook for Text-to-Speech functionality
 * Uses the Web Speech API to pronounce text in different languages
 */
export const useSpeech = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    // Check if browser supports speech synthesis
    if ('speechSynthesis' in window) {
      setIsSupported(true);

      // Load available voices
      const loadVoices = () => {
        const availableVoices = window.speechSynthesis.getVoices();
        setVoices(availableVoices);
      };

      loadVoices();

      // Chrome loads voices asynchronously
      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = loadVoices;
      }
    }
  }, []);

  /**
   * Speak text using Text-to-Speech
   * @param {string} text - Text to speak
   * @param {string} lang - Language code (e.g., 'ar-SA' for Arabic, 'en-US' for English)
   */
  const speak = (text, lang = 'ar-SA') => {
    if (!isSupported || !text) return;

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.9; // Slightly slower for learning
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Try to find a voice for the specified language
    const voice = voices.find(v => v.lang.startsWith(lang.split('-')[0])) ||
                  voices.find(v => v.lang === lang) ||
                  voices[0];

    if (voice) {
      utterance.voice = voice;
    }

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  /**
   * Stop any ongoing speech
   */
  const stop = () => {
    if (isSupported) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  return {
    isSupported,
    isSpeaking,
    speak,
    stop,
    voices
  };
};
